import { Account, ClientAccount } from '@lune-climate/lune'
import { ButtonNew, LabeledFormSectionNew, LuneThemeNew, Modal, Text } from '@lune-fe/lune-ui-lib'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import { Box, styled } from '@mui/material'
import { FC, ReactElement, useCallback, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'

import ConditionalTooltip from 'components/ConditionalTooltip'

const { palette } = LuneThemeNew

const LogoImage = styled('img')(() => ({
    maxWidth: '100%',
    maxHeight: '100%',
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
}))

const UploadArea = styled(Box)<{ hasLogo: boolean }>(({ theme, hasLogo }) =>
    theme.unstable_sx({
        border: `1px ${hasLogo ? 'solid' : 'dashed'} ${palette.Surface600}`,
        backgroundColor: palette.Surface100,
        borderRadius: '12px',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '142px',
        transition: 'all 0.2s ease',
        cursor: hasLogo ? 'default' : 'pointer',
        ...(!hasLogo && {
            '&:hover': {
                borderColor: palette.Grey400,
                backgroundColor: palette.Grey100,
            },
        }),
    }),
)

interface AccountLogoUploadProps {
    account: Account | ClientAccount
    readOnly?: boolean
    forbiddenTooltip?: FC<{ children: ReactElement; contentWidth?: boolean }>
    onLogoUpdate: (file: File | null) => void
    updating?: boolean
}

const AccountLogoUpload: FC<AccountLogoUploadProps> = ({
    account,
    readOnly,
    forbiddenTooltip,
    onLogoUpdate,
    updating = false,
}) => {
    const currentLogo = account.logo ?? null
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [logoRemoveModalOpen, setLogoRemoveModalOpen] = useState<boolean>(false)
    const hasLogo = !!currentLogo

    const handleLogoChange = useCallback(
        (file: File | null) => {
            if (readOnly) {
                return
            }
            onLogoUpdate(file)
        },
        [readOnly, onLogoUpdate],
    )

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (acceptedFiles.length > 0 && !readOnly && !updating) {
                const file = acceptedFiles[0]
                handleLogoChange(file)
            }
        },
        [readOnly, updating, handleLogoChange],
    )

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: {
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
        },
        maxSize: 2 * 1000 * 1000, // 2 MB (decimal kilo, bytes)
        disabled: readOnly || updating,
        multiple: false,
        noClick: hasLogo, // Disable click when logo exists, enable when no logo
    })

    const handleFileSelect = useCallback(() => {
        fileInputRef.current?.click()
    }, [])

    const handleFileInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0]
            if (file && !readOnly && !updating) {
                handleLogoChange(file)
            }
            // Reset input so same file can be selected again
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        },
        [readOnly, updating, handleLogoChange],
    )

    const handleRemoveLogo = useCallback(() => {
        if (!readOnly && !updating) {
            setLogoRemoveModalOpen(true)
        }
    }, [readOnly, updating, handleLogoChange])

    return (
        <LabeledFormSectionNew title={'Logo'} subTile={'Displayed at the top of customer pages'}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    maxWidth: '422px',
                }}
            >
                <div {...(hasLogo ? {} : getRootProps())}>
                    <input
                        {...getInputProps()}
                        ref={fileInputRef}
                        onChange={handleFileInputChange}
                        style={{ display: 'none' }}
                    />
                    <UploadArea hasLogo={hasLogo}>
                        {hasLogo ? (
                            <LogoImage src={currentLogo} alt="Account logo" />
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <FileUploadOutlinedIcon
                                    sx={{
                                        color: `TextSecondary`,
                                        width: '20px',
                                        height: '20px',
                                    }}
                                />
                                <Text variant={'button'} sx={{ color: `TextSecondary` }}>
                                    Add logo
                                </Text>
                            </Box>
                        )}
                    </UploadArea>
                </div>

                {hasLogo && (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            alignItems: 'center',
                        }}
                    >
                        <ConditionalTooltip
                            show={!!readOnly && !!forbiddenTooltip}
                            tooltip={forbiddenTooltip!}
                        >
                            <ButtonNew
                                size={'sm'}
                                style={'fill'}
                                variant={'secondary'}
                                leadingIcon={<FileUploadOutlinedIcon />}
                                onClick={handleFileSelect}
                                disabled={readOnly || updating}
                            >
                                Change logo
                            </ButtonNew>
                        </ConditionalTooltip>
                        <ConditionalTooltip
                            show={!!readOnly && !!forbiddenTooltip}
                            tooltip={forbiddenTooltip!}
                        >
                            <ButtonNew
                                size={'sm'}
                                style={'floating'}
                                onClick={handleRemoveLogo}
                                disabled={readOnly || updating}
                            >
                                Remove logo
                            </ButtonNew>
                        </ConditionalTooltip>
                    </Box>
                )}

                <Text variant={'body3'} sx={{ color: palette.TextSecondary }}>
                    JPG, JPEG or PNG up to 2MB. For best results, use a horizontal logo with a
                    transparent background.
                </Text>
                <Modal
                    maxWidth={'sm'}
                    fullWidth
                    open={logoRemoveModalOpen}
                    onClose={() => setLogoRemoveModalOpen(false)}
                    header={
                        <Box
                            sx={{
                                width: '100%',
                            }}
                        >
                            <Text variant={'h5'} sx={{ mb: 2, textAlign: 'center' }}>
                                Remove logo
                            </Text>
                            <Text
                                variant={'body2'}
                                sx={{
                                    textAlign: 'center',
                                    color: `TextSecondary`,
                                }}
                            >
                                Once removed, your account will no longer display a custom logo. You
                                can upload a replacement at any time.
                            </Text>
                        </Box>
                    }
                >
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            justifyContent: 'center',
                            button: {
                                minWidth: '120px',
                            },
                        }}
                    >
                        <ButtonNew
                            variant={'secondary'}
                            onClick={() => setLogoRemoveModalOpen(false)}
                        >
                            Cancel
                        </ButtonNew>
                        <ButtonNew
                            onClick={() => {
                                handleLogoChange(null)
                                setLogoRemoveModalOpen(false)
                            }}
                        >
                            Remove logo
                        </ButtonNew>
                    </Box>
                </Modal>
            </Box>
        </LabeledFormSectionNew>
    )
}

export default AccountLogoUpload
